/**
 * QL-539 — what the transfer form's From account has available: its cash left
 * once the flow's trades and the other planned transfers run (the overview's
 * `cashLeft`, never below 0). The form reads it with the transfer being edited
 * left out, so a transfer's own amount is not counted against its account.
 * Moving more than this would need a deposit on the account.
 */

import type { AccountCashOverviewRow, CashAccount } from "@repo/shared/types";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./cash-transfer-available-cash.i18n";
import { type FormatNumberFn, formatWhole, wholeAvailableCash } from "./types";

type CashTransferAvailableCashProps = {
  account: CashAccount;
  /** The account's overview row; undefined while the overview loads. */
  row: AccountCashOverviewRow | undefined;
  fmt: FormatNumberFn;
};

/**
 * The From account's available amount: "4 000 NOK", or "…" while the overview
 * loads. Rounded down like "Use all" fills it (QL-742).
 */
export function availableAmount(
  account: CashAccount,
  row: AccountCashOverviewRow | undefined,
  fmt: FormatNumberFn,
): string {
  return row ? `${formatWhole(wholeAvailableCash(row.cashLeft), fmt)} ${account.currency}` : "…";
}

export function CashTransferAvailableCash({ account, row, fmt }: CashTransferAvailableCashProps) {
  const t = useT(T);
  const bankCash = `${formatWhole(account.cashAmount, fmt)} ${account.currency}`;

  return (
    <p
      className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground"
      data-testid="cash-transfer-available-cash"
    >
      <span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="cursor-help underline decoration-dotted underline-offset-4"
            >
              {t.availableCash}
            </button>
          </TooltipTrigger>
          <TooltipContent>{t.availableCashHint(bankCash)}</TooltipContent>
        </Tooltip>
        : <span className="tabular-nums">{availableAmount(account, row, fmt)}</span>
      </span>
      {/* An amount without an FX rate is left out of the figure — say so, like
          the overview row does, rather than show it as complete. */}
      {row?.conversionFailed && <span className="basis-full">{t.conversionFailed}</span>}
    </p>
  );
}
